import { EventType } from 'matrix-js-sdk/src';
import { Room } from 'matrix-js-sdk/src/matrix';
import { useEffect } from 'react';
import { FieldNamesMarkedBoolean } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useToast from 'contexts/ToastProvider/useToast';
import { RoomAccessType } from 'hooks/roomCreation/useRoomAccessInitialState';
import useIsRoomInCurrentWorkspace from 'hooks/useIsRoomInCurrentWorkspace';
import useNoPermissionToast from 'hooks/useNoPermissionToast';
import useRoomAccessChange, { BotValidatedState } from 'hooks/useRoomAccessChange/useRoomAccessChange';
import useScopedUserPowerLevels from 'hooks/useScopedUserPowerLevels/useScopedUserPowerLevels';
import useToggleWaitingList from 'hooks/useToggleWaitingList/useToggleWaitingList';
import useUpdateRoomCanonicalAlias from 'hooks/useUpdateRoomCanonicalAlias';
import logger from 'utils/logging/faro';
import { CustomMeedioMatrixEvents, isPrivateRoom } from 'utils/matrixUtils';
import {
  canEditMeetingDetailsForRoom,
  canSendMeetingRoomStateEvent,
  canToggleWaitingListForRoom,
} from 'utils/meetingPermissionGates';
import { PermissionId, workspacesConfig } from 'utils/powerLevelsConfig';
import { getCanonicalParentRoom } from 'utils/roomHierarchyUtils';

import { CreateRoomProps, UpdateRoomProps } from './utils';

const SHARED_ROOM_THRESHOLD = workspacesConfig[PermissionId.WorkspaceInviteMembers].powerLevel;

const isPrivateRoomToAccessType = (isPrivateRoom: boolean): RoomAccessType => (isPrivateRoom ? 'team' : 'shared');

interface UseEditMatrixRoomReturn {
  editRoom: (props: UpdateRoomProps) => Promise<void>;
  accessChangeState: BotValidatedState;
  canSwitchToShared: boolean;
  resetAccessChange: () => void;
}

const useEditMatrixRoom = (
  dirtyFields: Partial<Readonly<FieldNamesMarkedBoolean<CreateRoomProps>>>,
  options: { roomId?: string | null; room?: { roomId: string } | null } = {}
): UseEditMatrixRoomReturn => {
  const { t } = useTranslation();
  const { matrixClient } = useMatrixContext();
  const { pushToast } = useToast();
  const { toggleWaitingList } = useToggleWaitingList();
  const { updateRoomCanonicalAlias } = useUpdateRoomCanonicalAlias();
  const { getIsRoomInCurrentWorkspace } = useIsRoomInCurrentWorkspace();
  const pushNoPermissionToast = useNoPermissionToast();

  const roomIdForHook = options.roomId ?? options.room?.roomId ?? '';
  const room = roomIdForHook ? matrixClient.getRoom(roomIdForHook) : null;
  const workspaceRoomId = room ? getCanonicalParentRoom(room)?.roomId ?? null : null;
  const workspacePowerLevels = useScopedUserPowerLevels(workspaceRoomId);
  const canSwitchToShared = workspacePowerLevels.workspace >= SHARED_ROOM_THRESHOLD;

  const {
    state: accessChangeState,
    changeAccess: triggerAccessChange,
    reset: resetAccessChange,
  } = useRoomAccessChange(roomIdForHook);

  useEffect(() => {
    if (accessChangeState === 'rejected') {
      pushToast({ title: t('room_settings.errors.workspace_pl_required'), variant: 'error' });
    } else if (accessChangeState === 'timeout') {
      pushToast({ title: t('room_settings.errors.timeout'), variant: 'error' });
    }
  }, [accessChangeState, pushToast, t]);

  const canEditDetails = (targetRoom: Room, actorUserId: string | null) =>
    canEditMeetingDetailsForRoom(targetRoom, actorUserId, workspacePowerLevels.meeting, getIsRoomInCurrentWorkspace);

  const canEditDetailsField = (targetRoom: Room, actorUserId: string | null, eventType: EventType | string) =>
    !!actorUserId &&
    canEditDetails(targetRoom, actorUserId) &&
    canSendMeetingRoomStateEvent(targetRoom, actorUserId, eventType);

  const editRoom = async ({
    roomAlias,
    roomName,
    room: targetRoom,
    isWaitingListEnabled,
    description,
    isPrivateRoom: isPrivateRoomValue,
  }: UpdateRoomProps) => {
    try {
      const { roomId } = targetRoom;
      const currentAlias = targetRoom.getCanonicalAlias();
      const actorUserId = matrixClient.getUserId();

      if (dirtyFields.roomName) {
        if (canEditDetailsField(targetRoom, actorUserId, EventType.RoomName)) {
          await matrixClient.sendStateEvent(roomId, EventType.RoomName, { name: roomName });
        } else {
          pushNoPermissionToast();
        }
      }

      if (dirtyFields.description) {
        if (canEditDetailsField(targetRoom, actorUserId, EventType.RoomTopic)) {
          await matrixClient.sendStateEvent(roomId, EventType.RoomTopic, { topic: description || '' });
        } else {
          pushNoPermissionToast();
        }
      }

      if (dirtyFields.isWaitingListEnabled) {
        const canToggleWaitingList = canToggleWaitingListForRoom(
          targetRoom,
          actorUserId,
          workspacePowerLevels.meeting,
          getIsRoomInCurrentWorkspace
        );
        const canEditPrivateMeetingDetails = !isPrivateRoom(targetRoom) || canEditDetails(targetRoom, actorUserId);
        const canChangeRoomAccess =
          !!actorUserId && canSendMeetingRoomStateEvent(targetRoom, actorUserId, CustomMeedioMatrixEvents.ROOM_ACCESS);
        const canSendJoinRules =
          !!actorUserId && canSendMeetingRoomStateEvent(targetRoom, actorUserId, EventType.RoomJoinRules);
        const useAccessEventForWaitingList = !!workspaceRoomId;

        if (
          canToggleWaitingList &&
          canEditPrivateMeetingDetails &&
          (useAccessEventForWaitingList ? canChangeRoomAccess : canSendJoinRules)
        ) {
          await toggleWaitingList({
            isWaitingListEnabled: !isWaitingListEnabled,
            roomId,
            workspaceId: workspaceRoomId,
            changeRoomAccess: useAccessEventForWaitingList ? triggerAccessChange : undefined,
          });
        } else {
          pushNoPermissionToast();
        }
      }

      if (dirtyFields.isPrivateRoom) {
        const isPrivate = !!isPrivateRoomValue;
        const accessType = isPrivateRoomToAccessType(isPrivate);

        if (!canEditDetailsField(targetRoom, actorUserId, CustomMeedioMatrixEvents.IS_PRIVATE_ROOM)) {
          pushNoPermissionToast();
        } else if (
          accessType === 'shared' &&
          (!canSwitchToShared ||
            !actorUserId ||
            !canSendMeetingRoomStateEvent(targetRoom, actorUserId, CustomMeedioMatrixEvents.ROOM_ACCESS))
        ) {
          pushToast({ title: t('room_settings.errors.workspace_pl_required'), variant: 'error' });
        } else {
          void triggerAccessChange(accessType);
          await matrixClient.sendStateEvent(roomId, CustomMeedioMatrixEvents.IS_PRIVATE_ROOM, {
            isPrivate,
          });
        }
      }

      if (roomAlias) {
        if (canEditDetailsField(targetRoom, actorUserId, EventType.RoomCanonicalAlias)) {
          await updateRoomCanonicalAlias({ roomId, alias: roomAlias, currentAlias });
        } else {
          pushNoPermissionToast();
        }
      }
    } catch (error) {
      logger.error('Error editing room:', error);
      throw error;
    }
  };

  return { editRoom, accessChangeState, canSwitchToShared, resetAccessChange };
};

export default useEditMatrixRoom;
